// === CUSTOM: bundle-builder - root ===

import { announceError, announceStatus } from "theme";

const MONEY_FORMAT_FALLBACK = "{{amount}}";

function readMoneyFormat() {
  const themeVars = window.themeVariables;
  const fmt = themeVars && themeVars.settings && themeVars.settings.moneyFormat;
  return fmt || MONEY_FORMAT_FALLBACK;
}

function formatMoney(amountCents, format) {
  const fmt = format || readMoneyFormat();
  const intAmount = Math.round(Number(amountCents) || 0);
  const decAmount = (intAmount / 100).toFixed(2);
  const intPart = String(Math.round(intAmount / 100));

  return fmt
    .replace(
      "{{amount_no_decimals_with_comma_separator}}",
      intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    )
    .replace(
      "{{amount_with_comma_separator}}",
      decAmount
        .replace(".", ",")
        .replace(/(\d)(?=(\d{3})+(?:,|$))/g, "$1.")
    )
    .replace(
      "{{amount_no_decimals}}",
      intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    )
    .replace(
      "{{amount}}",
      decAmount.replace(/(\d)(?=(\d{3})+\.)/g, "$1,")
    );
}

function generateBundleId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return "bdl_" + crypto.randomUUID();
  }
  return "bdl_" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
}

function readBundleStrings(scopeEl) {
  const node = scopeEl && scopeEl.querySelector("script[data-bundle-strings]");
  if (!node) return {};
  try {
    return JSON.parse(node.textContent);
  } catch (err) {
    return {};
  }
}

class BundleForm extends HTMLElement {
  constructor() {
    super();
    this._selections = new Map();
    this._strings = {};
    this._form = null;
    this._atc = null;
    this._steps = [];
    this._abortController = null;
    this._atcLabelDefault = "";
  }

  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._form = this.querySelector("form");
    this._atc = this.querySelector(".bundle-builder__atc");
    this._steps = Array.from(this.querySelectorAll("bundle-step"));
    this._strings = readBundleStrings(this);

    this._atcLabelDefault =
      (this._atc && this._atc.textContent.trim()) ||
      this._strings.addToCartLabel ||
      "";

    if (this._form) {
      this._form.addEventListener("submit", (e) => this._onSubmit(e), { signal });
    }
    this.addEventListener("bundlestep:change", (e) => this._onStepChange(e), { signal });

    /* === CUSTOM: bundle-builder - accordion exclusivity polyfill === */
    const detailsEls = this.querySelectorAll(".bundle-step__details");
    for (const det of detailsEls) {
      det.addEventListener(
        "toggle",
        () => {
          if (!det.open) return;
          for (const other of detailsEls) {
            if (other !== det && other.open) other.open = false;
          }
        },
        { signal }
      );
    }
    /* === END CUSTOM: bundle-builder - accordion exclusivity polyfill === */

    this._recomputeAndDispatch();
  }

  disconnectedCallback() {
    if (this._abortController) this._abortController.abort();
  }

  getSelections() {
    return new Map(this._selections);
  }

  _onStepChange(event) {
    const sel = event.detail;
    if (!sel || !sel.stepId) return;
    this._selections.set(sel.stepId, sel);
    this._recomputeAndDispatch();

    /* === CUSTOM: bundle-builder - auto-advance === */
    const currentStep = this._steps.find((s) => s.dataset.stepId === sel.stepId);
    if (!currentStep) return;

    const selectionEl = currentStep.querySelector("[data-bundle-step-selection]");
    if (selectionEl) selectionEl.textContent = sel.productTitle || "";
    currentStep.setAttribute("data-has-selection", "true");

    const currentDetails = currentStep.querySelector(".bundle-step__details");
    const nextIncomplete = this._findFirstIncomplete();

    if (nextIncomplete) {
      const nextDetails = nextIncomplete.querySelector(".bundle-step__details");
      if (nextDetails) nextDetails.open = true;
      const firstRadio = nextIncomplete.querySelector(
        "input.bundle-step-product__input:not([disabled])"
      );
      if (firstRadio) firstRadio.focus();
    } else if (this._atc) {
      this._atc.focus();
    }

    if (currentDetails) currentDetails.open = false;
    /* === END CUSTOM: bundle-builder - auto-advance === */
  }

  _recomputeAndDispatch() {
    let componentSumCents = 0;
    let compareAtSumCents = 0;
    for (const step of this._steps) {
      const sel = this._selections.get(step.dataset.stepId);
      const radios = Array.from(step.querySelectorAll("input.bundle-step-product__input"));
      const selected = radios.find((radio) => radio.checked);
      const available = radios.filter((radio) => !radio.disabled);
      const candidates = available.length ? available : radios;
      const referencePrices = candidates.map((radio) => Number(radio.dataset.compareAtPrice || radio.dataset.price) || 0);
      compareAtSumCents += selected
        ? Number(selected.dataset.compareAtPrice || selected.dataset.price) || 0
        : referencePrices.length ? Math.min(...referencePrices) : 0;
      if (sel) {
        componentSumCents += Number(sel.priceCents) || 0;
      } else {
        componentSumCents += Number(step.dataset.cheapestCents) || 0;
      }
    }
    const fixedPriceCents = Number(this.dataset.bundleFixedPriceCents) || 0;
    const hasFixedPrice = fixedPriceCents > 0;
    const savingsCents = Math.max(0, compareAtSumCents - fixedPriceCents);
    const complete = this._isComplete();

    if (this._atc && this._atc.getAttribute("aria-busy") !== "true") {
      this._atc.disabled = !complete;
      this._atc.setAttribute("aria-disabled", String(!complete));
    }

    this.dispatchEvent(
      new CustomEvent("bundle:total-change", {
        bubbles: true,
        composed: true,
        detail: {
          componentSumCents,
          compareAtSumCents,
          fixedPriceCents,
          savingsCents,
          hasFixedPrice,
          complete
        }
      })
    );
  }

  _isComplete() {
    return this._steps.every((step) => {
      if (step.dataset.required !== "true") return true;
      return this._selections.has(step.dataset.stepId);
    });
  }

  _findFirstIncomplete() {
    return (
      this._steps.find(
        (step) =>
          step.dataset.required === "true" &&
          !this._selections.has(step.dataset.stepId)
      ) || null
    );
  }

  _onSubmit(event) {
    event.preventDefault();
    if (this._atc && this._atc.getAttribute("aria-busy") === "true") return;

    if (!this._isComplete()) {
      this._handleIncompleteSubmit();
      return;
    }

    this._submitToCart();
  }

  _handleIncompleteSubmit() {
    const firstIncomplete = this._findFirstIncomplete();
    if (firstIncomplete) {
      /* === CUSTOM: bundle-builder - force-open before focus === */
      const detailsEl = firstIncomplete.querySelector(".bundle-step__details");
      if (detailsEl && !detailsEl.open) detailsEl.open = true;
      /* === END CUSTOM: bundle-builder - force-open before focus === */
      firstIncomplete.setAttribute("aria-invalid", "true");
      firstIncomplete.scrollIntoView({ behavior: "smooth", block: "center" });
      const firstRadio = firstIncomplete.querySelector(
        "input.bundle-step-product__input:not([disabled])"
      );
      if (firstRadio) firstRadio.focus();
    }
    const msg = this._strings.errorStepMissing || "Please complete every step";
    this.dispatchEvent(
      new CustomEvent("cart:error", { bubbles: true, detail: { error: msg } })
    );
    document.dispatchEvent(
      new CustomEvent("toast:show", {
        detail: { message: msg, tone: "error" }
      })
    );
    announceError(msg);
  }

  async _submitToCart() {
    const bundleId = generateBundleId();
    const bundleTitle =
      this._strings.bundleProductTitle || this.dataset.bundleProductTitle || "";

    const fixedPriceCents = String(
      Number(this.dataset.bundleFixedPriceCents) || 0
    );

    const items = [];

    for (const step of this._steps) {
      const sel = this._selections.get(step.dataset.stepId);
      if (!sel) continue;
      items.push({
        id: Number(sel.variantId),
        quantity: 1,
        properties: {
          Bundle: bundleTitle,
          _bundle_id: bundleId,
          _bundle_discount_eligible: "1",
          _bundle_step: sel.stepTitle || "",
          _bundle_fixed_price_cents: fixedPriceCents
        }
      });
    }

    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent("theme:loading:start", { bubbles: true })
    );

    this._setBusy(true);

    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/";
    const sectionsParam = [...sectionsToBundle].join(",");

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json"
        },
        body: JSON.stringify({ items, sections: sectionsParam })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw responseJson;
      }

      const cartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: "application/json" }
      });
      const cartContent = await cartResponse.json();
      cartContent.sections = responseJson.sections || {};

      this._form.dispatchEvent(
        new CustomEvent("variant:add", {
          bubbles: true,
          detail: { items: responseJson.items, cart: cartContent }
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: { baseEvent: "variant:add", cart: cartContent }
        })
      );

      const announceLabel =
        this._strings.addToCartLabel || this._atcLabelDefault;
      announceStatus(announceLabel);
    } catch (errorJson) {
      const msg =
        this._strings.errorOosAtSubmit ||
        "One or more selections is no longer in stock.";
      this._form.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: { error: msg }
        })
      );
      document.dispatchEvent(
        new CustomEvent("toast:show", {
          detail: { message: msg, tone: "error" }
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", { bubbles: true })
      );
      announceError(msg);
    } finally {
      this._setBusy(false);
      document.documentElement.dispatchEvent(
        new CustomEvent("theme:loading:end", { bubbles: true })
      );
    }
  }

  _setBusy(isBusy) {
    if (!this._atc) return;
    if (isBusy) {
      this._atc.setAttribute("aria-busy", "true");
      this._atc.disabled = true;
      if (this._strings.addToCartBusy) {
        this._atc.textContent = this._strings.addToCartBusy;
      }
    } else {
      this._atc.removeAttribute("aria-busy");
      this._atc.disabled = !this._isComplete();
      this._atc.setAttribute("aria-disabled", String(this._atc.disabled));
      this._atc.textContent = this._atcLabelDefault;
    }
  }
}

class BundleStep extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._cards = [];
  }

  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._cards = Array.from(this.querySelectorAll(".bundle-step-product"));

    for (const card of this._cards) {
      const radio = card.querySelector("input.bundle-step-product__input");
      if (!radio) continue;

      radio.addEventListener("change", () => this._onRadioChange(radio), {
        signal
      });
    }

    this.addEventListener(
      "change",
      () => {
        if (this.getAttribute("aria-invalid") === "true") {
          this.removeAttribute("aria-invalid");
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    if (this._abortController) this._abortController.abort();
  }

  _onRadioChange(radio) {
    if (!radio.checked) return;

    const card = radio.closest(".bundle-step-product");
    const titleEl = card && card.querySelector(".bundle-step-product__title");

    const detail = {
      stepId: radio.dataset.stepId,
      stepTitle: this.dataset.stepTitle || "",
      productId: radio.dataset.productId,
      productHandle: radio.dataset.productHandle,
      variantId: radio.dataset.variantId,
      priceCents: Number(radio.dataset.price) || 0,
      productTitle: (titleEl && titleEl.textContent.trim()) || "",
      variantTitle: radio.dataset.variantTitle || ""
    };

    this.dispatchEvent(
      new CustomEvent("bundlestep:change", {
        bubbles: true,
        composed: true,
        detail
      })
    );
  }
}

class BundleTotal extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._fromPriceCents = 0;
    this._fixedPriceCents = 0;
    this._hasFixedPrice = false;
    this._strings = {};
    this._bundleForm = null;
    this._savingsRow = null;
    /* === CUSTOM: bundle-builder - track ALL [data-bundle-compare-at] nodes (top price-list + summary) === */
    this._compareAtNodes = null;
    /* === END CUSTOM === */
    this._savingsAmountNode = null;
  }

  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._fromPriceCents = parseInt(this.dataset.fromPrice || "0", 10);
    this._fixedPriceCents = parseInt(this.dataset.fixedPrice || "0", 10);
    this._hasFixedPrice = this.dataset.hasFixedPrice === "true";
    this._bundleForm = this.closest("bundle-form");
    this._strings = readBundleStrings(this._bundleForm);

    if (this._bundleForm) {
      /* === CUSTOM: bundle-builder - scope queries to the section root so the top price-list (which sits ABOVE bundle-form) is also reachable; querySelectorAll on compare-at picks up both top + summary occurrences === */
      const sectionRoot = this._bundleForm.closest("[data-bundle-id]") || this._bundleForm;
      this._savingsRow = sectionRoot.querySelector("[data-bundle-savings]");
      this._compareAtNodes = sectionRoot.querySelectorAll("[data-bundle-compare-at]");
      this._savingsAmountNode = sectionRoot.querySelector(
        "[data-bundle-savings-amount]"
      );
      /* === END CUSTOM === */
    }

    document.addEventListener(
      "bundle:total-change",
      (e) => {
        if (e.target !== this._bundleForm) return;
        this._render(e.detail);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    if (this._abortController) this._abortController.abort();
  }

  _render({ componentSumCents, compareAtSumCents, fixedPriceCents, savingsCents, hasFixedPrice, complete }) {
    const primaryCents = hasFixedPrice
      ? fixedPriceCents
      : componentSumCents || this._fromPriceCents;
    const primaryStr = formatMoney(primaryCents);
    const template = complete
      ? this._strings.totalComplete || "{{ price }}"
      : this._strings.totalIncomplete || "From {{ price }}";
    this.textContent = template.replace("{{ price }}", primaryStr);

    if (!this._savingsRow) return;

    if (hasFixedPrice && savingsCents > 0) {
      /* === CUSTOM: bundle-builder - update every [data-bundle-compare-at] occurrence (top price-list + summary) === */
      if (this._compareAtNodes && this._compareAtNodes.length) {
        const compareAtStr = formatMoney(compareAtSumCents);
        this._compareAtNodes.forEach((node) => {
          node.textContent = compareAtStr;
        });
      }
      /* === END CUSTOM === */
      if (this._savingsAmountNode) {
        const savingsTemplate = this._strings.savings || "Save {{ amount }}";
        this._savingsAmountNode.textContent = savingsTemplate.replace(
          "{{ amount }}",
          formatMoney(savingsCents)
        );
      }
      this._savingsRow.removeAttribute("hidden");
    } else {
      this._savingsRow.setAttribute("hidden", "");
    }
  }
}

if (!customElements.get("bundle-form")) {
  customElements.define("bundle-form", BundleForm);
}
if (!customElements.get("bundle-step")) {
  customElements.define("bundle-step", BundleStep);
}
if (!customElements.get("bundle-total")) {
  customElements.define("bundle-total", BundleTotal);
}

// === END CUSTOM: bundle-builder - root ===

// === CUSTOM: bundle-builder - cart group component ===

class BundleCartGroup extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._removeBtn = null;
    this._lineKeys = [];
    this._bundleId = "";
  }

  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._bundleId = this.dataset.bundleId || "";
    try {
      this._lineKeys = JSON.parse(this.dataset.lineKeys || "[]").filter(Boolean);
    } catch (err) {
      this._lineKeys = [];
    }

    this._removeBtn = this.querySelector("[data-bundle-cart-group-remove]");
    if (this._removeBtn) {
      this._removeBtn.addEventListener(
        "click",
        (e) => this._onRemoveBundle(e),
        { signal }
      );
    }

    this._applyA11yPatch();
  }

  disconnectedCallback() {
    if (this._abortController) this._abortController.abort();
  }

  _applyA11yPatch() {
    const removeButtons = this.querySelectorAll(".line-item__remove-button");
    for (const btn of removeButtons) {
      btn.setAttribute("tabindex", "-1");
      btn.setAttribute("aria-hidden", "true");
    }
  }

  _setBusy(isBusy) {
    if (!this._removeBtn) return;
    if (isBusy) {
      this._removeBtn.setAttribute("aria-busy", "true");
      this._removeBtn.disabled = true;
    } else {
      this._removeBtn.removeAttribute("aria-busy");
      this._removeBtn.disabled = false;
    }
  }

  _onRemoveBundle(event) {
    event.preventDefault();
    if (this._removeBtn && this._removeBtn.getAttribute("aria-busy") === "true") return;
    this._cascadeRemove();
  }

  async _cascadeRemove() {
    // === CUSTOM: bundle-builder - fetch fresh keys to avoid stale data-line-keys ===
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/";

    let updates = {};
    try {
      const cartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: "application/json" }
      });
      const cart = await cartResponse.json();
      for (const item of cart.items) {
        if (item.properties && item.properties._bundle_id === this._bundleId) {
          updates[item.key] = 0;
        }
      }
    } catch (err) {
      for (const key of this._lineKeys) {
        updates[key] = 0;
      }
    }

    if (Object.keys(updates).length === 0) return;
    // === END CUSTOM: bundle-builder - fetch fresh keys ===

    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent("theme:loading:start", { bubbles: true })
    );

    this._setBusy(true);

    const sectionsParam = [...sectionsToBundle].join(",");

    try {
      const response = await fetch(`${root}cart/update.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ updates, sections: sectionsParam })
      });

      if (!response.ok) {
        const errorJson = await response.json().catch(() => ({}));
        throw errorJson;
      }

      // === CUSTOM: bundle-builder - cart page detection via pathname (themeVariables.template is undefined) ===
      const isCartPage = /\/cart$/.test(window.location.pathname);
      // === END CUSTOM: bundle-builder - cart page detection ===

      const announcement = this.dataset.cascadeAnnouncement || "";
      if (announcement) announceStatus(announcement);

      if (isCartPage) {
        window.location.reload();
        return;
      }

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", { bubbles: true })
      );
    } catch (errorJson) {
      const msg =
        (errorJson && errorJson.description) ||
        this.dataset.errorMessage ||
        "";
      if (msg) {
        document.dispatchEvent(
          new CustomEvent("toast:show", {
            detail: { message: msg, tone: "error" }
          })
        );
        announceError(msg);
      }
    } finally {
      this._setBusy(false);
      document.documentElement.dispatchEvent(
        new CustomEvent("theme:loading:end", { bubbles: true })
      );
    }
  }
}

if (!customElements.get("bundle-cart-group")) {
  customElements.define("bundle-cart-group", BundleCartGroup);
}

// === END CUSTOM: bundle-builder - cart group component ===
